import React from 'react'
import { getOperatorInfo } from './constants'

const KAMDataRow = ({ kam, bgHover }) => {
  const operatorInfo = getOperatorInfo(kam.operator_code)
  
  // Get color classes based on percentage
  const getPercentageColor = (value) => {
    if (value >= 100) return 'text-green-700 bg-green-50'
    if (value >= 80) return 'text-blue-700 bg-blue-50'
    if (value >= 60) return 'text-orange-700 bg-orange-50'
    return 'text-red-700 bg-red-50'
  }

  const forecastValue = kam.forecast_vs_goal_percent
  const achievementValue = kam.achievement_percent || 0

  // Synthetic "Nieprzypisane" row: sales with no KAM (WWW/TELE/central warehouse).
  // Goal/percent/visit metrics are meaningless for it, so render dashes.
  if (kam.is_unassigned) {
    const dash = <span className="text-gray-400 text-xs">—</span>
    return (
      <tr className={`${bgHover} transition-colors bg-gray-50/50`}>
        <td className="px-4 py-3 font-medium text-gray-500 italic whitespace-nowrap">
          <i className="fa-solid fa-circle-question mr-2 text-gray-400"></i>
          {kam.kam_name}
        </td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3">
          <span
            className="px-3 py-1 text-xs font-semibold text-white rounded-full opacity-60"
            style={{ backgroundColor: operatorInfo.color }}
          >
            {operatorInfo.display_name}
          </span>
        </td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-gray-500 font-semibold text-right">
          {(kam.actual_sales_mtd || 0).toLocaleString()}
          {kam.sales_per_group && Object.keys(kam.sales_per_group).length > 1 && (
            <div className="text-xs font-normal text-gray-400 mt-0.5">
              {Object.entries(kam.sales_per_group).map(([name, val]) => (
                <span key={name} className="mr-2">{name}: {Math.round(val)}</span>
              ))}
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
        <td className="px-4 py-3 text-right">{dash}</td>
      </tr>
    )
  }

  return (
    <tr className={`${bgHover} transition-colors`}>
      {/* KAM Name */}
      <td className="px-4 py-3 font-medium text-gray-800 whitespace-nowrap">
        <i className="fa-regular fa-user mr-2 text-gray-400"></i>
        {kam.kam_name || 'N/A'}
      </td>

      {/* Region */}
      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
        <i className="fa-solid fa-location-dot mr-2 text-gray-400"></i>
        {kam.region || 'N/A'}
      </td>

      {/* Operator */}
      <td className="px-4 py-3">
        <span
          className="px-3 py-1 text-xs font-semibold text-white rounded-full"
          style={{ backgroundColor: operatorInfo.color }}
        >
          {operatorInfo.display_name}
        </span>
      </td>

      {/* Goal MIN */}
      <td className="px-4 py-3 text-gray-800 font-semibold text-right">
        {(kam.goal_value || 0).toLocaleString()}
      </td>

      {/* Goal TARGET */}
      <td className="px-4 py-3 text-gray-600 text-right">
        {(kam.goal_value_target || kam.goal_value || 0).toLocaleString()}
      </td>

      {/* Sales MTD */}
      <td className="px-4 py-3 text-gray-800 font-semibold text-right">
        {(kam.actual_sales_mtd || 0).toLocaleString()}
        {kam.sales_per_group && Object.keys(kam.sales_per_group).length > 1 && (
          <div className="text-xs font-normal text-gray-400 mt-0.5">
            {Object.entries(kam.sales_per_group).map(([name, val]) => (
              <span key={name} className="mr-2">{name}: {Math.round(val)}</span>
            ))}
          </div>
        )}
      </td>

      {/* MTD % */}
      <td className="px-4 py-3 text-right">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPercentageColor(achievementValue)}`}>
          {achievementValue.toFixed(1)}%
        </span>
      </td>

      {/* Last 7 days */}
      <td className="px-4 py-3 text-gray-600 text-right">
        {(kam.last_7_days_sales || 0).toLocaleString()}
      </td>

      {/* Forecast EOM */}
      <td className="px-4 py-3 text-right">
        {forecastValue !== null && forecastValue !== undefined ? (
          <span className={`font-semibold ${forecastValue >= 80 ? 'text-amber-600' : 'text-red-600'}`}>
            <i className="fa-solid fa-bullseye mr-2"></i>
            {forecastValue.toFixed(1)}%
          </span>
        ) : (
          <span className="text-gray-400 text-xs">-</span>
        )}
      </td>

      {/* Visits this month */}
      <td className="px-4 py-3 text-gray-600 text-right">
        <i className="fa-solid fa-bullseye mr-2 text-green-500"></i>
        {kam.total_visits_month || 0}
      </td>

      {/* Unique salons */}
      <td className="px-4 py-3 text-gray-600 text-right">
        <i className="fa-solid fa-arrow-trend-up mr-2 text-blue-500"></i>
        {kam.unique_salons_month || 0}
      </td>

      {/* Completed visits */}
      <td className="px-4 py-3 text-gray-600 text-right">
        {kam.completed_visits_month || 0}
      </td>

      {/* Total visits alltime */}
      <td className="px-4 py-3 text-gray-600 text-right">
        <i className="fa-solid fa-layer-group mr-2 text-yellow-500"></i>
        {kam.total_visits_alltime || 0}
      </td>
    </tr>
  )
}

export default KAMDataRow


